import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';
import { fetchUserByWallet } from '../components/userUtils';
import CampChatModal from '../components/CampChatModal';
import CampGroupChatModal from '../components/CampGroupChatModal';

const THEMES = {
  dark: {
    bg: '#121212',
    contentBg: 'rgba(18, 18, 18, 0.98)',
    cardBg: 'rgba(255,255,255,0.05)',
    cardHoverBg: 'rgba(255,255,255,0.08)',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#707070',
  },
  light: {
    bg: '#f5f5f5',
    contentBg: 'rgba(255, 255, 255, 0.98)',
    cardBg: 'rgba(0,0,0,0.03)',
    cardHoverBg: 'rgba(0,0,0,0.06)',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textSecondary: '#555',
    textMuted: '#888',
  },
};

const getPath = (path) => {
  const isCampSubdomain = window.location.hostname === 'camp.ssalsoongi.com';
  if (isCampSubdomain) {
    return path.replace(/^\/camp/, '') || '/';
  }
  return path;
};

export default function CampMyPage() {
  const navigate = useNavigate();
  const { walletAddress, nickname, setNickname } = useCoin();
  const [activeTab, setActiveTab] = useState('profile');
  const [theme] = useState(() => {
    const savedMode = localStorage.getItem('campThemeMode');
    const savedTheme = localStorage.getItem('campTheme');
    if (savedMode === 'system' || !savedMode) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme || 'dark';
  });

  // Profile state
  const [userData, setUserData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false);
  const [characterLoading, setCharacterLoading] = useState(false);
  const [characterError, setCharacterError] = useState('');
  const [characters, setCharacters] = useState([]);

  // Posts state
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);

  // Chats state
  const [rooms, setRooms] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedGroupChat, setSelectedGroupChat] = useState(null);

  const colors = THEMES[theme];

  useEffect(() => {
    if (!walletAddress) {
      navigate(getPath('/camp'), { replace: true });
      return;
    }
    fetchUserData();
    fetchMyPosts();
    fetchRooms();
    fetchGroupChats();
  }, [walletAddress]);

  // === Profile functions ===
  const fetchUserData = async () => {
    setProfileLoading(true);
    try {
      const data = await fetchUserByWallet();
      if (data) {
        setUserData(data);
        setNewNickname(data.nickname || '');
        setProfileImageUrl(data.profile || null);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
    setProfileLoading(false);
  };

  const handleEditNickname = () => {
    setIsEditingNickname(true);
    setNewNickname(userData?.nickname || '');
    setNicknameError('');
  };

  const handleCancelEdit = () => {
    setIsEditingNickname(false);
    setNewNickname(userData?.nickname || '');
    setNicknameError('');
  };

  const handleSaveNickname = async () => {
    if (!newNickname.trim()) {
      setNicknameError('Please enter a nickname');
      return;
    }
    if (newNickname.length < 2 || newNickname.length > 20) {
      setNicknameError('Nickname must be 2-20 characters');
      return;
    }
    const nicknameRegex = /^[가-힣a-zA-Z0-9_]+$/;
    if (!nicknameRegex.test(newNickname)) {
      setNicknameError('Only Korean, English, numbers, and _ allowed');
      return;
    }

    setSavingNickname(true);
    setNicknameError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setNicknameError('Please login again');
        setSavingNickname(false);
        return;
      }

      const response = await supabase.functions.invoke('update-nickname', {
        body: { nickname: newNickname },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (response.error) {
        if (response.error.message?.includes('already taken')) {
          setNicknameError('This nickname is already taken');
        } else {
          setNicknameError('Failed to change nickname');
        }
        setSavingNickname(false);
        return;
      }

      setUserData({ ...userData, nickname: newNickname });
      setNickname(newNickname);
      setIsEditingNickname(false);
      alert('Nickname changed successfully!');
    } catch (error) {
      console.error('Failed to change nickname:', error);
      setNicknameError('Failed to change nickname');
    }
    setSavingNickname(false);
  };

  const handleProfileImageClick = async () => {
    if (!walletAddress) return;
    setCharacterError('');
    setCharacters([]);
    setIsCharacterModalOpen(true);
    setCharacterLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCharacterError('Login required.');
        setCharacterLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('getUserCharacters', {
        body: { userId: walletAddress },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        console.error(error);
        setCharacterError('Failed to load characters.');
      } else {
        const payload = data || {};
        const charactersFromApi =
          payload?.data?.characters ||
          payload?.data?.data?.characters ||
          [];
        const mapped = charactersFromApi.map((c) => ({
          id: c.assetKey || c.tokenId || c.name,
          name: c.name,
          level: c.data?.level,
          jobCode: c.data?.jobCode,
          world: c.data?.world,
          imageUrl: c.data?.imageUrl,
        }));
        setCharacters(mapped);
      }
    } catch (err) {
      console.error(err);
      setCharacterError('Failed to load characters.');
    } finally {
      setCharacterLoading(false);
    }
  };

  const handleSelectCharacter = async (character) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        alert('Login required.');
        return;
      }

      const { error } = await supabase.functions.invoke('set-profile', {
        body: { profileImageUrl: character.imageUrl, characterName: character.name },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        console.error('set-profile error:', error);
        alert('Failed to set profile.');
        return;
      }

      if (character.imageUrl) setProfileImageUrl(character.imageUrl);
      setIsCharacterModalOpen(false);
    } catch (e) {
      console.error('set-profile exception:', e);
      alert('Error setting profile.');
    }
  };

  // === Posts functions ===
  const fetchMyPosts = async () => {
    setPostsLoading(true);
    try {
      const { data, error } = await supabase
        .from('camp_posts')
        .select('*')
        .eq('author_wallet', walletAddress)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Failed to fetch my posts:', err);
    } finally {
      setPostsLoading(false);
    }
  };

  // === Chats functions ===
  const fetchRooms = async () => {
    setChatsLoading(true);
    try {
      const { data, error } = await supabase
        .from('camp_chat_rooms')
        .select('*')
        .or(`user1_wallet.eq.${walletAddress},user2_wallet.eq.${walletAddress}`)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      setRooms(data || []);
    } catch (err) {
      console.error('Failed to fetch chat rooms:', err);
    } finally {
      setChatsLoading(false);
    }
  };

  const fetchGroupChats = async () => {
    try {
      // Find group chats where current user is a member
      const { data: memberData, error: memErr } = await supabase
        .from('camp_group_chat_members')
        .select('group_chat_id')
        .eq('wallet_address', walletAddress);
      if (memErr) throw memErr;

      if (!memberData || memberData.length === 0) {
        setGroupChats([]);
        return;
      }

      const chatIds = memberData.map(m => m.group_chat_id);
      const { data: chats, error: chatErr } = await supabase
        .from('camp_group_chats')
        .select('*')
        .in('id', chatIds)
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (chatErr) throw chatErr;

      // Fetch member counts
      const chatsWithCounts = await Promise.all(
        (chats || []).map(async (chat) => {
          const { count } = await supabase
            .from('camp_group_chat_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_chat_id', chat.id);
          return { ...chat, member_count: count || 0 };
        })
      );
      setGroupChats(chatsWithCounts);
    } catch (err) {
      console.error('Failed to fetch group chats:', err);
    }
  };

  const getOtherUser = (room) => {
    if (room.user1_wallet === walletAddress) {
      return { wallet: room.user2_wallet, nickname: room.user2_nickname || 'Anonymous' };
    }
    return { wallet: room.user1_wallet, nickname: room.user1_nickname || 'Anonymous' };
  };

  const handleOpenChat = (room) => {
    const other = getOtherUser(room);
    setSelectedRoom({
      targetWallet: other.wallet,
      targetNickname: other.nickname,
      postId: room.post_id,
      postTitle: room.post_title,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'posts', label: 'My Posts' },
    { id: 'chats', label: 'Chats' },
  ];

  // If a group chat is selected, show the group chat modal
  if (selectedGroupChat) {
    return (
      <CampGroupChatModal
        isOpen={true}
        onClose={() => {
          setSelectedGroupChat(null);
          fetchGroupChats();
        }}
        groupChatId={selectedGroupChat.id}
        postId={selectedGroupChat.post_id}
        postTitle={selectedGroupChat.name}
      />
    );
  }

  // If a chat room is selected, show the chat modal
  if (selectedRoom) {
    return (
      <CampChatModal
        isOpen={true}
        onClose={() => {
          setSelectedRoom(null);
          fetchRooms();
        }}
        targetWallet={selectedRoom.targetWallet}
        targetNickname={selectedRoom.targetNickname}
        postId={selectedRoom.postId}
        postTitle={selectedRoom.postTitle}
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: colors.bg,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 430,
        minHeight: '100vh',
        background: colors.contentBg,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 0 40px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <button
            onClick={() => navigate(getPath('/camp'))}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '10px 16px',
              cursor: 'pointer',
              color: colors.text,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            ← Back
          </button>
          <span style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
            My Page
          </span>
          <div style={{ width: 70 }} />
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '14px 0',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id
                  ? '2px solid #667eea'
                  : '2px solid transparent',
                color: activeTab === tab.id ? '#667eea' : colors.textMuted,
                fontSize: 14,
                fontWeight: activeTab === tab.id ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {activeTab === 'profile' && renderProfile()}
          {activeTab === 'posts' && renderPosts()}
          {activeTab === 'chats' && renderChats()}
        </div>
      </div>

      {/* Character Selection Modal */}
      {isCharacterModalOpen && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: theme === 'dark' ? '#111827' : '#fff',
              borderRadius: 16,
              padding: '20px', width: '100%', maxWidth: 640,
              maxHeight: '80vh', overflowY: 'auto',
              color: colors.text,
              boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setIsCharacterModalOpen(false)}
              style={{
                position: 'absolute', top: 12, right: 12,
                background: 'transparent', border: 'none',
                color: colors.textMuted, cursor: 'pointer', fontSize: 22,
              }}
            >
              ×
            </button>

            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 18, fontWeight: 600, color: colors.text }}>
              Select Character
            </h3>
            <p style={{ margin: 0, marginBottom: 16, fontSize: 13, color: colors.textMuted }}>
              Choose a character to use as your profile picture.
            </p>

            {characterLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: colors.textMuted }}>
                Loading...
              </div>
            ) : characterError ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: '#fca5a5', fontSize: 13 }}>
                {characterError}
              </div>
            ) : characters.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
                No characters found.
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 16, marginTop: 8,
                }}
              >
                {characters.map((ch) => (
                  <button
                    key={ch.id || ch.name}
                    onClick={() => handleSelectCharacter(ch)}
                    style={{
                      backgroundColor: theme === 'dark' ? '#020617' : colors.cardBg,
                      borderRadius: 18,
                      border: `1px solid ${colors.border}`,
                      padding: 14,
                      cursor: 'pointer', textAlign: 'center',
                      color: colors.text, fontSize: 12,
                      display: 'flex', flexDirection: 'column', gap: 10,
                      transition: 'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 14px 30px rgba(0,0,0,0.55)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div
                      style={{
                        width: 96, height: 96, borderRadius: 20,
                        margin: '0 auto', overflow: 'hidden',
                        background: theme === 'dark'
                          ? 'radial-gradient(circle at 30% 0%, #1f2937, #020617 60%)'
                          : 'radial-gradient(circle at 30% 0%, #f3f4f6, #e5e7eb 60%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {ch.imageUrl ? (
                        <img
                          src={ch.imageUrl}
                          alt={ch.name || 'character'}
                          style={{
                            width: '150%', height: '150%',
                            objectFit: 'contain', objectPosition: 'center center',
                            transform: 'translateY(-8px)',
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 13, color: colors.textMuted }}>
                          {ch.name || 'Character'}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setIsCharacterModalOpen(false)}
                style={{
                  padding: '8px 14px', borderRadius: 8,
                  backgroundColor: colors.cardBg, color: colors.text,
                  border: `1px solid ${colors.border}`, fontSize: 13, cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  // === Render functions ===
  function renderProfile() {
    if (profileLoading) {
      return (
        <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>
          Loading...
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Profile Image */}
        <div style={{ textAlign: 'center' }}>
          <div
            onClick={handleProfileImageClick}
            style={{
              width: 96, height: 96,
              borderRadius: '50%', overflow: 'hidden',
              margin: '0 auto 8px',
              border: `2px solid ${colors.border}`,
              cursor: 'pointer', position: 'relative',
              backgroundColor: colors.cardBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt="Profile"
                style={{
                  width: '150%', height: '150%',
                  objectFit: 'contain', objectPosition: 'center center',
                  transform: 'translateY(-8px)',
                }}
              />
            ) : (
              <span style={{ fontSize: 12, color: colors.textMuted }}>Select Character</span>
            )}
            <div
              style={{
                position: 'absolute', bottom: 0, width: '100%',
                padding: '4px 0',
                background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent)',
                color: '#fff', fontSize: 10, textAlign: 'center',
              }}
            >
              Change
            </div>
          </div>
        </div>

        {/* Nickname */}
        <div style={{
          background: colors.cardBg,
          borderRadius: 12,
          padding: 16,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 500 }}>Nickname</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isEditingNickname ? (
                <>
                  <input
                    type="text"
                    value={newNickname}
                    onChange={(e) => setNewNickname(e.target.value)}
                    style={{
                      padding: '6px 10px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                      maxWidth: 150,
                      background: colors.inputBg,
                      color: colors.text,
                    }}
                    placeholder="Enter nickname"
                    maxLength={20}
                    disabled={savingNickname}
                  />
                  <button
                    onClick={handleSaveNickname}
                    disabled={savingNickname}
                    style={{
                      padding: '6px 12px', borderRadius: 6,
                      background: '#667eea', color: '#fff', border: 'none',
                      cursor: savingNickname ? 'not-allowed' : 'pointer',
                      fontSize: 12, fontWeight: 600,
                      opacity: savingNickname ? 0.6 : 1,
                    }}
                  >
                    {savingNickname ? '...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={savingNickname}
                    style={{
                      padding: '6px 10px', borderRadius: 6,
                      background: colors.inputBg, color: colors.textSecondary, border: `1px solid ${colors.border}`,
                      cursor: savingNickname ? 'not-allowed' : 'pointer',
                      fontSize: 12,
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                    {userData?.nickname || nickname || '-'}
                  </span>
                  <button
                    onClick={handleEditNickname}
                    style={{
                      background: 'transparent', border: 'none',
                      cursor: 'pointer', padding: 4,
                      color: colors.textMuted, fontSize: 14,
                    }}
                    title="Change nickname"
                  >
                    ✏️
                  </button>
                </>
              )}
            </div>
          </div>
          {nicknameError && (
            <div style={{ color: '#f44336', fontSize: 12, marginTop: 8, textAlign: 'right' }}>
              {nicknameError}
            </div>
          )}
        </div>

        {/* Wallet Address */}
        <div style={{
          background: colors.cardBg,
          borderRadius: 12,
          padding: 16,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 500, flexShrink: 0 }}>Wallet</span>
            <span style={{
              fontSize: 12, color: colors.textSecondary,
              wordBreak: 'break-all', textAlign: 'right', marginLeft: 12,
            }}>
              {userData?.wallet_address || walletAddress}
            </span>
          </div>
        </div>
      </div>
    );
  }

  function renderPosts() {
    if (postsLoading) {
      return (
        <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>
          Loading...
        </div>
      );
    }

    if (posts.length === 0) {
      return (
        <div style={{
          background: colors.cardBg,
          borderRadius: 16,
          padding: 40,
          textAlign: 'center',
        }}>
          <p style={{ color: colors.textSecondary, fontSize: 14, margin: 0 }}>
            No posts yet.
          </p>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {posts.map(post => (
          <div
            key={post.id}
            onClick={() => navigate(getPath(`/camp/post/${post.id}`))}
            style={{
              background: colors.cardBg,
              borderRadius: 12,
              padding: 16,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = colors.cardHoverBg}
            onMouseLeave={(e) => e.currentTarget.style.background = colors.cardBg}
          >
            <div style={{
              fontSize: 15,
              fontWeight: 600,
              color: colors.text,
              marginBottom: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {post.title}
            </div>
            {(post.server || post.boss) && (
              <div style={{ fontSize: 12, color: '#667eea', marginBottom: 8, display: 'flex', gap: 8 }}>
                {post.server && <span>[{post.server}]</span>}
                {post.boss && <span>{post.boss}</span>}
              </div>
            )}
            <div style={{
              fontSize: 12,
              color: colors.textMuted,
              textAlign: 'right',
            }}>
              {formatDate(post.created_at)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderChats() {
    if (chatsLoading) {
      return (
        <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>
          Loading...
        </div>
      );
    }

    if (rooms.length === 0 && groupChats.length === 0) {
      return (
        <div style={{
          background: colors.cardBg,
          borderRadius: 16,
          padding: 40,
          textAlign: 'center',
        }}>
          <p style={{ color: colors.textSecondary, fontSize: 14, margin: 0 }}>
            No chats yet.
          </p>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Group Chats */}
        {groupChats.map((gc) => (
          <div
            key={`gc_${gc.id}`}
            onClick={() => setSelectedGroupChat(gc)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 12,
              cursor: 'pointer',
              transition: 'background 0.15s',
              background: 'transparent',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = colors.cardBg}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            {/* Group Avatar */}
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {gc.member_count || 0}P
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                    Party Chat
                  </span>
                  <span style={{
                    fontSize: 9, color: '#22c55e',
                    background: 'rgba(34,197,94,0.15)',
                    padding: '1px 5px', borderRadius: 4,
                    fontWeight: 600,
                  }}>
                    GROUP
                  </span>
                </div>
                <span style={{ fontSize: 11, color: colors.textMuted, flexShrink: 0 }}>
                  {formatTime(gc.last_message_at || gc.created_at)}
                </span>
              </div>
              {gc.name && (
                <div style={{
                  fontSize: 11, color: '#667eea', marginBottom: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {gc.name}
                </div>
              )}
              {gc.last_message && (
                <div style={{
                  fontSize: 12, color: colors.textMuted,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {gc.last_message}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* 1:1 Chats */}
        {rooms.map((room) => {
          const other = getOtherUser(room);
          return (
            <div
              key={room.id}
              onClick={() => handleOpenChat(room)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 12,
                cursor: 'pointer',
                transition: 'background 0.15s',
                background: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = colors.cardBg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {(other.nickname || '?')[0].toUpperCase()}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                    {other.nickname}
                  </span>
                  <span style={{ fontSize: 11, color: colors.textMuted, flexShrink: 0 }}>
                    {formatTime(room.last_message_at || room.created_at)}
                  </span>
                </div>
                {(room.post_title || room.applied_class) && (
                  <div style={{
                    fontSize: 11, color: '#667eea', marginBottom: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {room.post_title && <span>{room.post_title}</span>}
                    {room.applied_class && (
                      <span style={{
                        background: 'rgba(102,126,234,0.15)',
                        padding: '1px 6px',
                        borderRadius: 6,
                        fontSize: 10,
                        flexShrink: 0,
                      }}>
                        {room.applied_class}
                      </span>
                    )}
                  </div>
                )}
                {room.last_message && (
                  <div style={{
                    fontSize: 12, color: colors.textMuted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {room.last_message}
                  </div>
                )}
                {(room.user1_deleted || room.user2_deleted) && (
                  <div style={{
                    fontSize: 10, color: '#f44336', marginTop: 2,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {room.user1_deleted && room.user2_deleted
                      ? 'Both requested deletion'
                      : `Delete requested (${
                          (room.user1_wallet === walletAddress ? room.user1_deleted : room.user2_deleted) ? 'You' : other.nickname
                        })`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}
